/**
 * 敏感词管理工具函数
 */

import { validationRules } from './config';

/**
 * 格式化敏感词显示
 * @param {string} words - 敏感词字符串
 * @param {number} maxDisplay - 最大显示数量
 * @returns {string} 格式化后的字符串
 */
export function formatSensitiveWords(words, maxDisplay = 3) {
  if (!words) return '';
  
  const wordArray = words.split(/[,，]/).map(word => word.trim()).filter(word => word);
  
  if (wordArray.length <= maxDisplay) {
    return words;
  } else {
    return wordArray.slice(0, maxDisplay).join(', ') + `...等${wordArray.length}个`;
  }
}

/**
 * 格式化日期时间
 * @param {string|number} timestamp - 时间戳
 * @param {string} format - 格式化模式
 * @returns {string} 格式化后的日期时间
 */
export function formatDateTime(timestamp, format = 'YYYY-MM-DD HH:mm') {
  if (!timestamp) return '';
  
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return '';
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  
  switch (format) {
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`;
    case 'YYYY-MM-DD HH:mm':
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    case 'YYYY-MM-DD HH:mm:ss':
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    default:
      return `${year}-${month}-${day} ${hours}:${minutes}`;
  }
}

/**
 * 验证敏感词数据
 * @param {Object} data - 待验证的数据
 * @returns {Object} 验证结果 { isValid: boolean, errors: Array }
 */
export function validateSensitiveWordsData(data) {
  const errors = [];
  
  // 验证敏感词组名称
  if (!data.word_name || !data.word_name.trim()) {
    errors.push('敏感词组名称不能为空');
  } else if (data.word_name.length > 50) {
    errors.push('敏感词组名称长度不能超过50个字符');
  } else if (!validationRules.word_name.pattern.test(data.word_name)) {
    errors.push('敏感词组名称只能包含中文、英文、数字、下划线、中划线和空格');
  }
  
  // 验证描述
  if (data.description && data.description.length > 200) {
    errors.push('描述长度不能超过200个字符');
  }
  
  // 验证敏感词内容
  if (!data.sensitive_words || !data.sensitive_words.trim()) {
    errors.push('敏感词内容不能为空');
  } else if (data.sensitive_words.length > 1000) {
    errors.push('敏感词内容长度不能超过1000个字符');
  } else if (!validationRules.sensitive_words.pattern.test(data.sensitive_words)) {
    errors.push('敏感词内容只能包含中文、英文、数字、下划线、中划线、空格和逗号');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * 处理敏感词字符串，去重但保持原始顺序
 * @param {string} words - 敏感词字符串
 * @returns {string} 处理后的字符串
 */
export function processSensitiveWords(words) {
  if (!words) return '';
  
  // 分割、去重、保持原始顺序、重新组合
  const wordArray = words
    .split(/[,，]/)
    .map(word => word.trim())
    .filter(word => word)
    .filter((word, index, arr) => arr.indexOf(word) === index); // 去重
  
  return wordArray.join(',');
}

/**
 * 检查文本中是否包含敏感词
 * @param {string} text - 待检查的文本
 * @param {Array} sensitiveWordsList - 敏感词列表
 * @returns {Object} 检查结果 { hasSensitiveWords: boolean, foundWords: Array, highlightedText: string }
 */
export function checkTextForSensitiveWords(text, sensitiveWordsList) {
  if (!text || !sensitiveWordsList || sensitiveWordsList.length === 0) {
    return {
      hasSensitiveWords: false,
      foundWords: [],
      highlightedText: text
    };
  }
  
  const foundWords = [];
  let highlightedText = text;
  
  sensitiveWordsList.forEach(item => {
    const words = item.sensitive_words.split(/[,，]/).map(word => word.trim()).filter(word => word);
    
    words.forEach(word => {
      if (text.includes(word)) {
        foundWords.push({
          word,
          groupName: item.word_name,
          groupId: item.id
        });
        
        // 高亮敏感词（用*替换）
        const regex = new RegExp(word, 'g');
        highlightedText = highlightedText.replace(regex, '*'.repeat(word.length));
      }
    });
  });
  
  return {
    hasSensitiveWords: foundWords.length > 0,
    foundWords: foundWords.filter((item, index, arr) => 
      arr.findIndex(found => found.word === item.word) === index
    ), // 去重
    highlightedText
  };
}

/**
 * 生成敏感词统计信息
 * @param {Array} sensitiveWordsList - 敏感词列表
 * @returns {Object} 统计信息
 */
export function generateSensitiveWordsStats(sensitiveWordsList) {
  if (!sensitiveWordsList || sensitiveWordsList.length === 0) {
    return {
      totalGroups: 0,
      totalWords: 0,
      averageWordsPerGroup: 0,
      longestGroupName: '',
      shortestGroupName: '',
      mostCommonWords: []
    };
  }
  
  const totalGroups = sensitiveWordsList.length;
  let totalWords = 0;
  const allWords = [];
  const groupNameLengths = [];
  
  sensitiveWordsList.forEach(item => {
    const words = item.sensitive_words.split(/[,，]/).map(word => word.trim()).filter(word => word);
    totalWords += words.length;
    allWords.push(...words);
    groupNameLengths.push(item.word_name.length);
  });
  
  // 统计词频
  const wordFrequency = {};
  allWords.forEach(word => {
    wordFrequency[word] = (wordFrequency[word] || 0) + 1;
  });
  
  const mostCommonWords = Object.entries(wordFrequency)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 10)
    .map(([word, count]) => ({ word, count }));
  
  return {
    totalGroups,
    totalWords,
    averageWordsPerGroup: Math.round((totalWords / totalGroups) * 100) / 100,
    longestGroupName: sensitiveWordsList.reduce((longest, current) => 
      current.word_name.length > longest.word_name.length ? current : longest
    ).word_name,
    shortestGroupName: sensitiveWordsList.reduce((shortest, current) => 
      current.word_name.length < shortest.word_name.length ? current : shortest
    ).word_name,
    mostCommonWords
  };
}

/**
 * 导出敏感词数据为CSV格式
 * @param {Array} data - 敏感词数据
 * @param {string} filename - 文件名
 */
export function exportToCSV(data, filename = 'sensitive-words.csv') {
  if (!data || data.length === 0) {
    console.warn('没有数据可导出');
    return;
  }
  
  const headers = ['敏感词组名称', '描述', '敏感词内容', '创建时间', '更新时间'];
  const csvContent = [
    headers.join(','),
    ...data.map(item => [
      `"${item.word_name || ''}"`,
      `"${item.description || ''}"`,
      `"${item.sensitive_words || ''}"`,
      `"${formatDateTime(item.created_at, 'YYYY-MM-DD HH:mm:ss')}"`,
      `"${formatDateTime(item.updated_at, 'YYYY-MM-DD HH:mm:ss')}"`
    ].join(','))
  ].join('\n');
  
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

/**
 * 防抖函数
 * @param {Function} func - 要防抖的函数
 * @param {number} wait - 等待时间
 * @returns {Function} 防抖后的函数
 */
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * 节流函数
 * @param {Function} func - 要节流的函数
 * @param {number} limit - 限制时间
 * @returns {Function} 节流后的函数
 */
export function throttle(func, limit) {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

/**
 * 深拷贝对象
 * @param {*} obj - 要拷贝的对象
 * @returns {*} 拷贝后的对象
 */
export function deepClone(obj) {
  if (obj === null || typeof obj !== 'object') return obj;
  if (obj instanceof Date) return new Date(obj.getTime());
  if (obj instanceof Array) return obj.map(item => deepClone(item));
  if (typeof obj === 'object') {
    const clonedObj = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key]);
      }
    }
    return clonedObj;
  }
}

export default {
  formatSensitiveWords,
  formatDateTime,
  validateSensitiveWordsData,
  processSensitiveWords,
  checkTextForSensitiveWords,
  generateSensitiveWordsStats,
  exportToCSV,
  debounce,
  throttle,
  deepClone
}; 